import {
  type ComponentType,
  type ReactNode,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { useSnapshot } from 'valtio';

import { BaseStore } from './BaseStore';

// Config for specifying which parent stores to inject
type ParentStoreConfig<TParentDeps> = {
  [K in keyof TParentDeps]: React.Context<NonNullable<TParentDeps[K]> | null>;
};

interface ProviderConfig<
  TState extends object,
  TDeps extends object,
  TStore extends BaseStore<TState, TDeps>,
> {
  Store: new (deps: TDeps) => TStore;
  displayName: string;
  /**
   * Static dependencies to inject (e.g., global stores).
   * These are merged with resolved parent stores.
   */
  staticDeps?: Partial<TDeps>;
  /**
   * Map of dep key -> React Context to read from.
   * Parent stores are resolved from their contexts at runtime.
   */
  parentStores?: ParentStoreConfig<Partial<TDeps>>;
}

export function createStoreProvider<
  TState extends object,
  TDeps extends object,
  TStore extends BaseStore<TState, TDeps>,
>(config: ProviderConfig<TState, TDeps, TStore>) {
  const { Store, displayName, staticDeps, parentStores } = config;
  const StoreContext = createContext<TStore | null>(null);
  StoreContext.displayName = displayName;

  // Pre-compute entries array - this is fixed at createStoreProvider call time
  const parentStoreEntries = parentStores
    ? (Object.entries(parentStores) as [string, React.Context<unknown>][])
    : [];

  // Custom hook to resolve all parent contexts
  // Safe: parentStoreEntries is constant, so hook count is stable across renders
  function useParentStores(): Record<string, unknown> {
    const resolved: Record<string, unknown> = {};

    for (const [key, context] of parentStoreEntries) {
      // eslint-disable-next-line react-hooks/rules-of-hooks -- array length is fixed at creation time
      const value = useContext(context);
      if (!value) {
        throw new Error(
          `${displayName} requires parent store "${key}" but it was not found. ` +
            `Make sure ${displayName}Provider is rendered inside the parent's Provider.`,
        );
      }
      resolved[key] = value;
    }

    return resolved;
  }

  function Provider({ children }: { children: ReactNode }) {
    const resolvedParents = useParentStores();

    const storeRef = useRef<TStore | null>(null);

    if (!storeRef.current) {
      const deps = {
        ...staticDeps,
        ...resolvedParents,
      } as TDeps;

      storeRef.current = new Store(deps);
    }

    const store = storeRef.current;

    useEffect(() => {
      store.init();
      return () => store.destroy();
    }, [store]);

    return (
      <StoreContext.Provider value={store}>{children}</StoreContext.Provider>
    );
  }

  function useStore(): TStore {
    const store = useContext(StoreContext);
    if (!store) {
      throw new Error(
        `use${displayName} must be used within <${displayName}Provider>`,
      );
    }

    const snapshot = useSnapshot(store.state) as TState;

    // Create a stable Proxy that intercepts 'state' access
    // The snapshot ref is updated on each render but the Proxy is stable
    const snapshotRef = useRef(snapshot);
    snapshotRef.current = snapshot;

    const storeProxy = useMemo(
      () =>
        new Proxy(store, {
          get(target, prop) {
            if (prop === 'state') {
              return snapshotRef.current;
            }
            const value = Reflect.get(target, prop, target);
            // Bind methods to the original store so 'this' works correctly
            if (typeof value === 'function') {
              return value.bind(target);
            }
            return value;
          },
        }) as TStore,
      [store],
    );

    return storeProxy;
  }

  function useStoreApi(): TStore {
    const store = useContext(StoreContext);
    if (!store) {
      throw new Error(
        `use${displayName}Api must be used within <${displayName}Provider>`,
      );
    }

    return store;
  }

  /**
   * HOC that wraps a component with the Provider.
   * Allows using useStore directly without manual Provider wrapping.
   */
  function withProvider<TProps extends object>(
    Component: ComponentType<TProps>,
  ): ComponentType<TProps> {
    function WrappedComponent(props: TProps) {
      return (
        <Provider>
          <Component {...props} />
        </Provider>
      );
    }
    WrappedComponent.displayName = `with${displayName}(${Component.displayName || Component.name || 'Component'})`;
    return WrappedComponent;
  }

  return {
    Provider,
    useStore,
    useStoreApi,
    withProvider,
    Context: StoreContext,
  };
}
